import React, { useState } from 'react'
import styled from 'styled-components'
import { FileText, Loader2 } from 'lucide-react'
import {
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
} from '../../../molecules/Dashboard/SingleAlbumPage/albumFormSharedStyles'
import {
  PrimaryBtn,
  gradient,
} from '../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles'

interface RedeemContactTermsPanelProps {
  loading?: boolean
  onAgree: () => void
  onDecline: () => void
}

const PLATFORM = 'Kwik Memories'

/** Inline T&C for redeem code users section (not a modal). */
const RedeemContactTermsPanel: React.FC<RedeemContactTermsPanelProps> = ({
  loading = false,
  onAgree,
  onDecline,
}) => {
  const [hasReadTerms, setHasReadTerms] = useState(false)

  return (
    <Wrap>
      <CardHeader>
        <CardIcon $tone="#c94daa">
          <FileText size={18} />
        </CardIcon>
        <div>
          <CardTitle>Terms &amp; Conditions</CardTitle>
          <CardSubtitle>
            Consent for use of contact information — scroll to the bottom, confirm
            you have read everything, then agree to view redeem code users.
          </CardSubtitle>
        </div>
      </CardHeader>

      <TermsScroll>
        <Lead>
          Applicable to Studio Owners using {PLATFORM} (&quot;the Platform&quot;).
        </Lead>

        <Section>
          <SectionTitle>1. Purpose of Collection</SectionTitle>
          <P>
            By using the Platform to capture, import, or store the contact details
            (including but not limited to name, phone number, email address, and
            WhatsApp number) of leads, clients, or event guests (&quot;Contact
            Information&quot;), the Studio Owner acknowledges and agrees that such
            Contact Information may only be used for sales and marketing purposes
            directly related to the Studio&apos;s own photography and videography
            services. This includes, without limitation, sending quotes, booking
            confirmations, promotional offers, event reminders, review requests,
            and re-engagement or referral communications.
          </P>
        </Section>

        <Section>
          <SectionTitle>2. Studio Owner&apos;s Consent and Undertaking</SectionTitle>
          <P>The Studio Owner represents and warrants that:</P>
          <List>
            <li>
              They have obtained, or will obtain, the necessary consent from each
              individual whose Contact Information is uploaded to or captured
              through the Platform, in accordance with applicable law, before using
              that information for sales and marketing purposes.
            </li>
            <li>
              Contact Information will not be sold, rented, leased, shared, or
              otherwise disclosed to any third party, including other businesses,
              marketers, or data brokers, for any purpose unrelated to the
              Studio&apos;s own client servicing and marketing.
            </li>
            <li>
              Contact Information will not be used for any unlawful, deceptive,
              harassing, or unsolicited communication that violates applicable
              anti-spam, telemarketing, or data protection laws (including
              India&apos;s DPDP Act, TRAI regulations, GDPR, CAN-SPAM, or
              equivalent local law in the relevant jurisdiction).
            </li>
            <li>
              They will honor opt-out, unsubscribe, or data-deletion requests from
              any individual within the timeframe required by applicable law.
            </li>
            <li>
              They will maintain reasonable administrative, technical, and physical
              safeguards to protect Contact Information from unauthorized access,
              loss, misuse, or disclosure.
            </li>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Responsibility and Liability for Misuse</SectionTitle>
          <P>
            The Studio Owner acknowledges that the Platform is a tool provided to
            facilitate the Studio&apos;s own business operations, and that the
            Studio — together with its owner(s), partners, and authorized team
            members — is solely and fully responsible for how Contact Information
            uploaded, collected, or processed through the Platform is used.
          </P>
          <P>
            In the event of any misuse of Contact Information, including but not
            limited to unauthorized sharing, sale, unsolicited or non-consensual
            marketing, data breach caused by the Studio&apos;s own negligence, or
            any violation of applicable data protection or privacy law, full
            responsibility and liability shall rest solely with the Studio and its
            owner(s). This includes liability for any resulting complaints,
            regulatory penalties, fines, third-party claims, or legal proceedings.
          </P>
          <P>
            The Studio Owner agrees to indemnify and hold harmless {PLATFORM}, its
            founders, employees, and affiliates from and against any claims,
            damages, losses, liabilities, costs, and expenses (including reasonable
            legal fees) arising out of or connected to the Studio&apos;s misuse of
            Contact Information or breach of this consent clause.
          </P>
        </Section>

        <Section>
          <SectionTitle>4. Platform&apos;s Role</SectionTitle>
          <P>
            {PLATFORM} acts solely as a technology provider enabling the Studio to
            manage and communicate with its own leads and clients. The Platform
            does not independently use, sell, or share Studio Owners&apos; contact
            databases for its own marketing purposes, except as separately
            disclosed in the Platform&apos;s own Privacy Policy.
          </P>
        </Section>

        <Section>
          <SectionTitle>5. Consequences of Breach</SectionTitle>
          <P>
            Any confirmed misuse of Contact Information by a Studio Owner may
            result in suspension or termination of the Studio&apos;s account on the
            Platform, in addition to any other legal remedies available to affected
            individuals or regulatory authorities.
          </P>
        </Section>

        <Section>
          <SectionTitle>6. Acknowledgment</SectionTitle>
          <P>
            By creating an account, uploading contact data, or continuing to use
            the Platform, the Studio Owner confirms that they have read,
            understood, and agree to be bound by this clause as part of the
            Platform&apos;s Terms &amp; Conditions.
          </P>
        </Section>

        <Disclaimer>
          This is a general-purpose draft intended as a starting point for Terms
          &amp; Conditions. It is not legal advice. Please have it reviewed and
          adapted by a lawyer familiar with the data protection laws applicable in
          each market you serve before relying on it.
        </Disclaimer>

        <ReadCheckLabel htmlFor="redeem-terms-read">
          <ReadCheck
            id="redeem-terms-read"
            type="checkbox"
            checked={hasReadTerms}
            disabled={loading}
            onChange={(e) => setHasReadTerms(e.target.checked)}
          />
          <span>I have read all Terms &amp; Conditions</span>
        </ReadCheckLabel>
      </TermsScroll>

      <FooterActions>
        <DeclineBtn type="button" disabled={loading} onClick={onDecline}>
          Decline
        </DeclineBtn>
        <AgreeBtn
          type="button"
          disabled={loading || !hasReadTerms}
          onClick={onAgree}
        >
          {loading ? (
            <>
              <SpinIcon size={16} />
              Saving…
            </>
          ) : (
            'I Agree'
          )}
        </AgreeBtn>
      </FooterActions>
    </Wrap>
  )
}

export default RedeemContactTermsPanel

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
`

const TermsScroll = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
`

const Lead = styled.p`
  margin: 0 0 14px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
`

const Section = styled.section`
  margin-bottom: 14px;
`

const SectionTitle = styled.h3`
  margin: 0 0 6px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #171717;
`

const P = styled.p`
  margin: 0 0 8px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.55;
  color: rgba(23, 23, 23, 0.72);
`

const List = styled.ul`
  margin: 0 0 8px;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.55;
    color: rgba(23, 23, 23, 0.72);
  }
`

const Disclaimer = styled.p`
  margin: 8px 0 16px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(201, 77, 170, 0.06);
  border: 1px solid rgba(201, 77, 170, 0.12);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(23, 23, 23, 0.55);
`

const ReadCheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 4px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(201, 77, 170, 0.18);
  background: rgba(201, 77, 170, 0.04);
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: #171717;
  user-select: none;

  span {
    padding-top: 1px;
  }
`

const ReadCheck = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 1px;
  flex-shrink: 0;
  accent-color: #c94daa;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`

const FooterActions = styled.div`
  display: flex;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`

const DeclineBtn = styled.button`
  flex: 1;
  height: 46px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  color: rgba(23, 23, 23, 0.7);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(23, 23, 23, 0.22);
    color: #171717;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const AgreeBtn = styled(PrimaryBtn)`
  flex: 1.2;
  width: auto;
  background: ${gradient};
`

const SpinIcon = styled(Loader2)`
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`
